import React, { useState } from 'react';
import { Play, Clock, Users, BookOpen, Award, CheckCircle } from 'lucide-react';
import VideoModal from './VideoModal';
import { scrollToSection } from '../utils/navigation';

const ExpertFormation = () => {
  const [selectedVideo, setSelectedVideo] = useState<{title: string, description: string} | null>(null);
  
  const modules = [
    {
      title: "Histoire et Tradition",
      duration: "2h",
      description: "Découvrez l'histoire authentique de la pizza napolitaine et ses traditions séculaires",
      video: "module1_histoire.mp4"
    },
    {
      title: "Sélection des Ingrédients",
      duration: "3h",
      description: "Apprenez à choisir les meilleurs ingrédients : farine, tomates San Marzano, mozzarella di bufala",
      video: "module2_ingredients.mp4"
    },
    {
      title: "Technique de la Pâte",
      duration: "4h",
      description: "Maîtrisez l'art du pétrissage, de la fermentation et du façonnage de la pâte napolitaine",
      video: "module3_pate.mp4"
    },
    {
      title: "Façonnage et Étalage",
      duration: "3h",
      description: "Techniques traditionnelles d'étalage à la main, sans rouleau",
      video: "module4_facconnage.mp4"
    },
    {
      title: "Garniture et Assemblage",
      duration: "2h",
      description: "L'art de garnir une pizza napolitaine dans les règles de l'art",
      video: "module5_garniture.mp4"
    },
    {
      title: "Cuisson au Four à Bois",
      duration: "4h",
      description: "Maîtrisez la cuisson haute température (450°C) et les techniques de rotation",
      video: "module6_cuisson.mp4"
    }
  ];

  const certifications = [
    "Certification École de la Pizza Napolitaine",
    "Attestation de formation professionnelle",
    "Reconnaissance RNCP (en cours)",
    "Éligible CPF"
  ];

  return (
    <section id="expert-napolitaine" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Formation Expert Pizza Napolitaine
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Une formation complète et intensive pour devenir un véritable expert de la pizza napolitaine. 
            Apprenez les techniques authentiques transmises de génération en génération.
          </p>
        </div>

        {/* Formation Overview */}
        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          <div>
            <img 
              src="./images/chef_formation_1.jpeg" 
              alt="Formation pizzaiolo professionnel"
              className="w-full h-96 object-cover rounded-2xl shadow-lg"
            />
          </div>
          <div className="space-y-6">
            <h3 className="text-3xl font-bold text-gray-900">Pourquoi cette formation ?</h3>
            <p className="text-gray-600 leading-relaxed">
              La pizza napolitaine est inscrite au patrimoine culturel immatériel de l'UNESCO. 
              Cette formation vous permettra de maîtriser cet art culinaire exceptionnel et de vous démarquer 
              dans le monde de la restauration.
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-lg shadow-md">
                <Clock className="h-8 w-8 text-red-600 mb-2" />
                <div className="font-semibold">40 heures</div>
                <div className="text-sm text-gray-600">Formation intensive</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-md">
                <Users className="h-8 w-8 text-red-600 mb-2" />
                <div className="font-semibold">Max 6 élèves</div>
                <div className="text-sm text-gray-600">Groupe restreint</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-md">
                <BookOpen className="h-8 w-8 text-red-600 mb-2" />
                <div className="font-semibold">6 modules</div>
                <div className="text-sm text-gray-600">Apprentissage complet</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-md">
                <Award className="h-8 w-8 text-red-600 mb-2" />
                <div className="font-semibold">Certification</div>
                <div className="text-sm text-gray-600">Reconnue</div>
              </div>
            </div>
          </div>
        </div>

        {/* Modules de Formation */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">Programme de Formation</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {modules.map((module, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="relative">
                  <div className="bg-gradient-to-br from-red-600 to-red-800 h-48 flex items-center justify-center">
                    <Play className="h-16 w-16 text-white opacity-80" />
                  </div>
                  <div className="absolute top-4 right-4 bg-yellow-400 text-red-900 px-3 py-1 rounded-full text-sm font-semibold">
                    {module.duration}
                  </div>
                </div>
                <div className="p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-3">{module.title}</h4>
                  <p className="text-gray-600 mb-4">{module.description}</p>
                  <button 
                    onClick={() => setSelectedVideo({title: module.title, description: module.description})}
                    className="w-full bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <Play className="h-4 w-4" />
                    Voir la vidéo
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Certifications */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-8">Certifications Obtenues</h3>
          <div className="grid md:grid-cols-2 gap-6">
            {certifications.map((cert, index) => (
              <div key={index} className="flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                <span className="text-gray-700">{cert}</span>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <button 
              onClick={() => scrollToSection('inscription')}
              className="bg-yellow-400 text-red-900 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-yellow-300 transition-colors inline-block"
            >
              S'inscrire à la Formation Expert - 1800€
            </button>
          </div>
        </div>
      </div>
      
      {/* Video Modal */}
      <VideoModal 
        isOpen={selectedVideo !== null}
        onClose={() => setSelectedVideo(null)}
        title={selectedVideo?.title || ''}
        description={selectedVideo?.description || ''}
      />
    </section>
  );
};

export default ExpertFormation;